/**
 * TakeoverScrim — full-viewport blurred dialog wrapper (Plan 02-15 / UI-SPEC §10).
 *
 * Renders a `bg-dim backdrop-blur-md` scrim (#152: handoffens `--stell-dim`)
 * covering the viewport
 * with the takeover content centred inside. Tap-anywhere on the
 * backdrop OR Escape key dismisses; auto-dismiss is the parent's
 * responsibility (L4Takeover passes 5000ms + a setTimeout).
 *
 * a11y: role="dialog" + aria-modal="true". Children may include focus
 * traps if needed; for v1 the only interactive element is the implicit
 * tap-to-dismiss target (the scrim itself).
 *
 * Reduced motion: the scrim renders nothing motion-driven on its own —
 * `prefers-reduced-motion: reduce` users see the scrim instantly without
 * a fade. Phase 1 motion.css already collapses CSS transitions; we don't
 * double-up. (This paragraph used to be labelled "idle-timer
 * suppression", which it never described — the scrim takes no idle hold.
 * It doesn't need one: L4Takeover auto-dismisses after 5000ms, well
 * inside the 60s wall-tablet window. See useIdleHold for the components
 * that do hold — issue #29.)
 *
 * Variant `screen` (#155, Feiring-skissen): en hel, ugjennomsiktig skjerm på
 * lerretet (`paper-tex`, med gløden) i stedet for et dempet, uskarpt lag over
 * appen. Feiringene `godkjent` og `merke` bruker den; innholdet fyller da
 * skjermen og legger selv ut knappene nederst. Dialogrollen, Escape og
 * trykk-hvor-som-helst er de samme.
 *
 * The scrim does NOT use a portal — it's `fixed inset-0 z-50` instead.
 * For the family app's flat route topology, a portal adds zero value
 * over a top-stacking fixed div.
 */
import { useEffect, type ReactNode } from 'react';

interface TakeoverScrimProps {
  open: boolean;
  onDismiss: () => void;
  children?: ReactNode;
  /** `scrim` (standard) demper appen bak; `screen` er en hel skjerm. */
  variant?: 'scrim' | 'screen';
  /** id-en til overskriften som navngir dialogen. */
  labelledBy?: string;
}

const SURFACE: Record<NonNullable<TakeoverScrimProps['variant']>, string> = {
  scrim: 'flex items-center justify-center bg-dim backdrop-blur-md',
  screen: 'overflow-y-auto paper-tex',
};

export function TakeoverScrim({
  open,
  onDismiss,
  children,
  variant = 'scrim',
  labelledBy,
}: TakeoverScrimProps): ReactNode {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: Event) => {
      const ke = e as { key?: string };
      if (ke.key === 'Escape') {
        onDismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div
      data-testid="takeover-scrim"
      data-variant={variant}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onClick={onDismiss}
      className={`fixed inset-0 z-50 ${SURFACE[variant]}`}
    >
      {/* The inner content stops click propagation only when a future
        plan adds an interactive element that should NOT dismiss the
        scrim on tap. For v1 the entire surface is dismiss-on-tap per
        UI-SPEC §10 ("tap-anywhere"). */}
      {children}
    </div>
  );
}
